import type { BusinessType } from "../types";
import { primaryCategories } from "../data";
import { useState, useEffect } from "react";
import { emailSchema, otpSchema } from "../../../utils/zod/onboardingSchemas";
import { emailRegex } from "../../../lib/validations/commonSchemas";
import { apiService } from "../../../services/api";

import { Input } from "@/components/ui/input";
import { OtpModal } from "@/components/ui/otp-modal";
import { PhoneNumberInput } from "@/components/ui/phone-input";
import FieldInfo from "../../../components/base/FieldInfo";


interface StepBusinessInfoProps {
  errors: Record<string, string>;
  fullName: string;
  username: string;
  usernameEditing: boolean;
  email: string;
  emailOtpSent: boolean;
  emailOtpCode: string;
  emailVerified: boolean;
  emailOtpError: string;
  emailOtpCountdown: number;
  brandName: string;
  verifiedCompanyName: string;
  websiteUrl: string;
  websiteUrlStatus: "idle" | "validating" | "valid" | "invalid";
  businessDescription: string;
  businessType: BusinessType | null;
  primaryCategory: string;
  showCategoryDropdown: boolean;
  isManagedByAgency: boolean;
  selectedAgencyFromList: string | null;
  showAgencyDropdown: boolean;
  agencyNotListed: boolean;
  agencyName: string;
  agencyContactName: string;
  agencyEmail: string;
  agencyPhone: string;
  agencyWebsite: string;
  agencyId: string;
  getOnboardingData: (email: string) => Promise<any>;
  onAgencyId: (value: string) => void;
  onFullNameChange: (value: string) => void;
  onUsernameChange: (value: string) => void;
  onUsernameEditing: (value: boolean) => void;
  onEmailChange: (value: string) => void;
  onEmailOtpCodeChange: (value: string) => void;
  onSendEmailOtp: () => void | Promise<boolean>;
  onVerifyEmailOtp: (otp?: string) => void;
  onCloseEmailOtp: () => void;
  checkingForPreviousData?: boolean;
  onResendEmailOtp: () => void;
  onBrandNameChange: (value: string) => void;
  onVerifiedCompanyNameChange: (value: string) => void;
  onWebsiteUrlChange: (value: string) => void;
  onBusinessDescriptionChange: (value: string) => void;
  onBusinessTypeChange: (value: BusinessType | null) => void;
  onPrimaryCategoryChange: (value: string) => void;
  onShowCategoryDropdown: (value: boolean) => void;
  onIsManagedByAgency: (value: boolean) => void;
  onSelectedAgencyFromList: (value: string | null) => void;
  onShowAgencyDropdown: (value: boolean) => void;
  onAgencyNotListed: (value: boolean) => void;
  onAgencyName: (value: string) => void;
  onAgencyContactName: (value: string) => void;
  onAgencyEmail: (value: string) => void;
  onAgencyPhone: (value: string) => void;
  onAgencyWebsite: (value: string) => void;
  onRegenerateUsername: () => void;
  onEmailVerifiedChange?: (value: boolean) => void;
}

export default function StepBusinessInfo(props: StepBusinessInfoProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [otpLoading, setOtpLoading] = useState(false);
  const [descriptionLoading, setDescriptionLoading] = useState(false);
  const [scrapeError, setScrapeError] = useState("");
  const [slowHint, setSlowHint] = useState(false);
  const [usernameCheck, setUsernameCheck] = useState<{ status: 'idle' | 'loading' | 'available' | 'taken', message: string }>({ status: 'idle', message: '' });
  const [foresightAgencies, setForesightAgencies] = useState<
    {
      id: string;
      name: string;
      email: string;
    }[]
  >([]);
  const [countryCodes, setCountryCodes] = useState<Array<{ name: string; code: string; dialCode: string }>>([]);
  const [categorySearchQuery, setCategorySearchQuery] = useState("");

  useEffect(() => {
    if (!descriptionLoading) {
      setSlowHint(false);
      return;
    }
    const timer = setTimeout(() => setSlowHint(true), 45000);
    return () => clearTimeout(timer);
  }, [descriptionLoading]);

  // ── Company Verification Gate State ──────────────────────────────────────
  const [companyName, setCompanyName] = useState(props.verifiedCompanyName || props.brandName || "");
  const [companyVerifyLoading, setCompanyVerifyLoading] = useState(false);
  const [companyVerifyStatus, setCompanyVerifyStatus] = useState<
    'idle' | 'pending_onboarding' | 'not_registered' | 'already_onboarded' | 'verified'
  >((props.verifiedCompanyName || props.brandName) ? 'verified' : 'idle');
  const [companyVerifyMessage, setCompanyVerifyMessage] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");

  useEffect(() => {
    if (props.verifiedCompanyName) {
      setCompanyName(props.verifiedCompanyName);
      setCompanyVerifyStatus('verified');
    } else if (props.brandName && companyVerifyStatus === 'idle') {
      setCompanyName(props.brandName);
      setCompanyVerifyStatus('verified');
    }
  }, [props.verifiedCompanyName, props.brandName]);
  // Secret code popup
  const [showSecretCodeModal, setShowSecretCodeModal] = useState(false);
  const [secretCode, setSecretCode] = useState("");
  const [secretCodeLoading, setSecretCodeLoading] = useState(false);
  const [secretCodeError, setSecretCodeError] = useState("");
  // Stage gates
  const companyAndCodeVerified = companyVerifyStatus === 'verified';
  const allVerified = companyAndCodeVerified && props.emailVerified;
  // ─────────────────────────────────────────────────────────────────────────

  const handleVerifyCompanyName = async () => {
    if (!companyName.trim()) return;
    setCompanyVerifyLoading(true);
    setCompanyVerifyMessage("");
    try {
      const res = await apiService.verifyCompanyIdentity(companyName.trim());
      if (res.status === 'pending_onboarding') {
        setCompanyVerifyStatus('pending_onboarding');
        setMaskedEmail(res.masked_email || "");
        setShowSecretCodeModal(true);
      } else if (res.status === 'already_onboarded') {
        setCompanyVerifyStatus('already_onboarded');
        setCompanyVerifyMessage("This company has already been onboarded.");
      } else {
        setCompanyVerifyStatus('not_registered');
        setCompanyVerifyMessage("No registration found for this company. Please register first.");
      }
    } catch {
      setCompanyVerifyStatus('not_registered');
      setCompanyVerifyMessage("Could not verify company. Please try again.");
    } finally {
      setCompanyVerifyLoading(false);
    }
  };

  const handleVerifySecretCode = async () => {
    if (!secretCode.trim()) return;
    setSecretCodeLoading(true);
    setSecretCodeError("");
    try {
      const res = await apiService.verifySecretCode(companyName.trim(), secretCode.trim());
      if (res.status === 'verified') {
        setCompanyVerifyStatus('verified');
        setShowSecretCodeModal(false);
        if (res.full_name) {
          props.onFullNameChange(res.full_name);
        }
        props.onBrandNameChange(companyName.trim());
        props.onVerifiedCompanyNameChange(companyName.trim());
      }
    } catch (err: any) {
      const detail = err?.detail || err?.message || "Invalid secret code. Please check your email and try again.";
      setSecretCodeError(detail === 'SECRET_CODE_EXPIRED' ? 'Your secret code has expired. Please contact support.' :
        detail === 'SECRET_CODE_INVALID' ? 'Incorrect secret code. Please check your email.' : detail);
    } finally {
      setSecretCodeLoading(false);
    }
  };


  useEffect(() => {
    fetchAgencies();
    const fetchCountryCodes = async () => {
      try {
        const response = await apiService.getCountryCodes();
        const apiCountryCodes = response?.data?.country_codes;
        if (
          Array.isArray(apiCountryCodes) &&
          apiCountryCodes.length > 0
        ) {
          setCountryCodes(apiCountryCodes);
        }
      } catch (error) {
        console.error("Failed to fetch country codes", error);
      }
    };
    fetchCountryCodes();
  }, []);

  useEffect(() => {
    if (!props.username) {
      setUsernameCheck({ status: 'idle', message: '' });
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setUsernameCheck({ status: 'loading', message: '' });
        const res = await apiService.checkUsername({
          username: props.username
        });
        if (res.message === "username is available") {
          setUsernameCheck({ status: 'available', message: res.message });
        } else {
          setUsernameCheck({ status: 'taken', message: res.message || "user name is already taken" });
        }
      } catch (error) {
        setUsernameCheck({ status: 'idle', message: '' });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [props.username]);

  const [emailCheck, setEmailCheck] = useState<{
    status: 'idle' | 'loading' | 'available' | 'taken';
    message: string;
  }>({ status: 'idle', message: '' });

  useEffect(() => {
    if (!props.email || props.emailVerified) {
      setEmailCheck({ status: 'idle', message: '' });
      return;
    }

    const isValidFormat = emailRegex.test(props.email);
    if (!isValidFormat) {
      setEmailCheck({ status: 'idle', message: '' });
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setEmailCheck({ status: 'loading', message: '' });
        const res = await apiService.checkEmail({
          email: props.email
        });
        if (res.success !== false && res.message !== "email id is alredy taken") {
          setEmailCheck({ status: 'available', message: 'Email is available' });
        } else {
          setEmailCheck({ status: 'taken', message: res.message || "email id is already taken" });
          if (props.setShowEmailExistsModal && props.setEmailExistsMessage) {
            props.setEmailExistsMessage(res.message || "email id is already taken");
            props.setShowEmailExistsModal(true);
          }
        }
      } catch (error) {
        setEmailCheck({ status: 'idle', message: '' });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [props.email, props.emailVerified]);


  const fetchAgencies = async () => {
    try {
      const response = await apiService.getAgencies();

      console.log(response.data);

      const formattedAgencies = response.data.agencies.map((agency: any) => ({
        id: agency.id,
        name: agency.name,
        email: agency.email,
      }));

      setForesightAgencies(formattedAgencies);
    } catch (error) {
      console.log("Failed to fetch agencies", error);
    }
  };

  const handleSendOtp = async () => {
    const result = emailSchema.safeParse({
      email: props.email,
    });

    // ZOD VALIDATION
    if (!result.success) {
      setErrors({
        email: result.error.flatten().fieldErrors.email?.[0] || "",
      });

      return;
    }

    if (emailCheck.status === 'taken') {
      setErrors({
        email: emailCheck.message || "email id is already taken",
      });
      return;
    }


    try {
      // clear old errors
      setErrors({});
      setOtpLoading(true);

      const allowed = await props.onSendEmailOtp();
      if (allowed === false) {
        setOtpLoading(false);
        return;
      }

      setOtpLoading(false);
    } catch (error: any) {
      setOtpLoading(false);
      console.log(error);

      setErrors({
        email: error.message || "Failed to send OTP",
      });
    }
  };

  const handleVerifyOtp = async (currentOtp?: string) => {
    const otpToUse = currentOtp || props.emailOtpCode;
    const result = otpSchema.safeParse({
      otp: otpToUse,
    });

    if (!result.success) {
      throw new Error(result.error.flatten().fieldErrors.otp?.[0] || "Invalid OTP");
    }

    try {
      setErrors({});

      const response = await apiService.verifyOtp({
        type: 'email',
        value: props.email,
        otp: otpToUse
      });

      if (response.message === "OTP verified successfully") {
        props.onVerifyEmailOtp(otpToUse);
      } else {
        throw new Error("Invalid code. Please try again.");
      }
    } catch (error: any) {
      throw new Error(error.message || "Invalid OTP");
    }
  };

  const handleDescription = async () => {
    try {
      if (!props.websiteUrl) {
        setScrapeError("Please enter a website URL first");
        return;
      }

      setScrapeError("");
      setDescriptionLoading(true);

      const res = await apiService.scrapeUrl(props.websiteUrl);

      console.log(res);

      if (res.status === "success") {
        props.onBusinessDescriptionChange(res.description);
      } else {
        throw new Error(res.error || res.message || "Failed to generate description (URL may be unreachable)");
      }
      setDescriptionLoading(false);
    } catch (error: any) {
      setDescriptionLoading(false);
      console.error("Auto-Fill failed:", error);
      setScrapeError("Couldn't generate a description. Please try again.");
    }
  };


  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Business Info</h2>
      </div>

      <div className="border-t border-gray-100"></div>

      {/* Identity Block */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-teal-500/10 flex items-center justify-center">
            <i className="ri-user-line text-teal-600 text-sm"></i>
          </div>
          Identity
        </h3>

        <div className="space-y-5">

          {/* ── Company Name + Verification Gate ── */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
              <span>Company Name</span> <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={companyName}
                onChange={(e) => {
                  setCompanyName(e.target.value);
                  setCompanyVerifyStatus('idle');
                  setCompanyVerifyMessage('');
                }}
                disabled={companyVerifyStatus === 'verified'}
                placeholder="Enter your registered company name"
                className={`flex-1 px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors
                  ${
                    companyVerifyStatus === 'verified'
                      ? 'bg-green-50 border-green-400 text-green-800 cursor-not-allowed'
                      : companyVerifyStatus === 'not_registered' || companyVerifyStatus === 'already_onboarded'
                      ? 'border-red-400 focus:ring-red-400'
                      : 'border-gray-200 focus:ring-teal-500 focus:border-transparent'
                  }`
                }
              />
              {companyVerifyStatus === 'verified' ? (
                <div className="flex items-center gap-1 text-green-600 text-sm font-medium px-3 py-2.5 bg-green-50 border border-green-300 rounded-lg">
                  <i className="ri-check-line text-base"></i> Verified
                </div>
              ) : (
                <button
                  type="button"
                  disabled={companyVerifyLoading || !companyName.trim()}
                  onClick={handleVerifyCompanyName}
                  className="px-4 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  {companyVerifyLoading ? (
                    <span className="flex items-center gap-1.5">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Checking...
                    </span>
                  ) : 'Verify'}
                </button>
              )}
            </div>
            {companyVerifyMessage && (
              <p className={`text-xs mt-1.5 ${
                companyVerifyStatus === 'not_registered' || companyVerifyStatus === 'already_onboarded'
                  ? 'text-red-500'
                  : 'text-green-600'
              }`}>
                {companyVerifyMessage}
              </p>
            )}
            {companyVerifyStatus === 'verified' && (
              <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
                <i className="ri-shield-check-line"></i> Company verified — you may now complete onboarding
              </p>
            )}
          </div>

          {/* ── Secret Code Popup Modal ── */}
          {showSecretCodeModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
              <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4 relative">
                <button
                  onClick={() => { setShowSecretCodeModal(false); setSecretCode(''); setSecretCodeError(''); }}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>

                <div className="flex flex-col items-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-teal-500/10 flex items-center justify-center mb-3">
                    <i className="ri-lock-password-line text-teal-600 text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Enter Your Secret Code</h3>
                  <p className="text-sm text-gray-500 text-center mt-2">
                    Check the payment confirmation email you received for your secret code.
                  </p>
                  {maskedEmail && (
                    <p className="text-xs text-gray-400 mt-1">
                      Sent to: <span className="font-mono text-teal-600">{maskedEmail}</span>
                    </p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 mb-1.5 block">Secret Code</label>
                  <input
                    type="text"
                    value={secretCode}
                    onChange={(e) => { setSecretCode(e.target.value.toUpperCase()); setSecretCodeError(''); }}
                    placeholder="e.g. A3F7K2P9XQ1B"
                    maxLength={12}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  {secretCodeError && (
                    <p className="text-xs text-red-500 mt-1.5">{secretCodeError}</p>
                  )}
                </div>

                <button
                  type="button"
                  disabled={secretCodeLoading || !secretCode.trim()}
                  onClick={handleVerifySecretCode}
                  className="w-full py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white rounded-xl text-sm font-semibold transition-colors"
                >
                  {secretCodeLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Verifying...
                    </span>
                  ) : 'Verify Secret Code'}
                </button>
              </div>
            </div>
          )}

          {/* Full Name */}
          <div className={!companyAndCodeVerified ? 'opacity-40 pointer-events-none select-none' : ''}>

            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
              <span>Full Name</span> <span className="text-red-500">*</span>
              <FieldInfo fieldKey="fullName" />
            </label>
            <input
              type="text"
              value={props.fullName}
              onChange={(e) => props.onFullNameChange(e.target.value.replace(/[^a-zA-Z\s]/g, ''))}
              placeholder="Enter your name"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            {props.errors.fullName && (
              <p className="text-xs text-red-500 mt-1">
                {props.errors.fullName}
              </p>
            )}
            {/* <p className="text-xs text-gray-400 mt-1">
              Used for account personalization
            </p> */}
          </div>

          {/* Username */}
          <div className={!companyAndCodeVerified ? 'opacity-40 pointer-events-none select-none' : ''}>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
              <span>Username</span> <span className="text-red-500">*</span>
              <FieldInfo fieldKey="username" />
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={props.username}
                onChange={(e) => {
                  props.onUsernameChange(
                    e.target.value.replace(/[^a-zA-Z0-9_]/g, ""),
                  );
                  props.onUsernameEditing(true);
                }}
                placeholder="johndoe123"
                className={`flex-1 px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors ${usernameCheck.status === 'available' ? 'border-green-400 bg-green-50 focus:ring-green-500' :
                  usernameCheck.status === 'taken' ? 'border-red-400 bg-red-50 focus:ring-red-500' :
                    'border-gray-200 focus:ring-teal-500 focus:border-transparent'
                  }`}
              />
              <button
                type="button"
                onClick={props.onRegenerateUsername}
                className="px-3 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-600 transition-colors cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
              >
                {usernameCheck.status === 'loading' ? (
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <i className="ri-refresh-line"></i>
                )}
                Regenerate
              </button>
            </div>
            {props.errors.username && (
              <p className="text-xs text-red-500 mt-1">
                {props.errors.username}
              </p>
            )}
            {usernameCheck.status === 'available' && !props.errors.username && (
              <p className="text-xs text-green-600 mt-1">
                <i className="ri-check-line mr-1"></i> {usernameCheck.message}
              </p>
            )}
            {usernameCheck.status === 'taken' && !props.errors.username && (
              <p className="text-xs text-red-500 mt-1">
                <i className="ri-error-warning-line mr-1"></i> {usernameCheck.message}
              </p>
            )}
            {/* <p className="text-xs text-gray-400 mt-1">
              Unique identifier — auto-generated from your name, but you can
              edit it.
            </p> */}
          </div>

          {/* Dashboard Access Email with OTP Verification */}
          <div className={!companyAndCodeVerified ? 'opacity-40 pointer-events-none select-none' : ''}>
            {companyAndCodeVerified && !props.emailVerified && (
              <div className="mb-2 flex items-center gap-1.5 text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs font-medium">
                <i className="ri-mail-check-line text-sm"></i>
                Email verification required to unlock the rest of the form.
              </div>
            )}
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
              <span>Business Email</span> <span className="text-red-500">*</span>
              <FieldInfo fieldKey="email" />
            </label>
            <div className="flex items-center gap-2">
              <input
                type="email"
                value={props.email}
                onChange={(e) => {
                  props.onEmailChange(e.target.value)
                  // getOnboardingData()
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    props.getOnboardingData(props.email);
                  }
                }}
                placeholder="john@example.com"
                disabled={false}
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
              />
              {!props.emailVerified && (
                <button
                  type="button"
                  onClick={() => handleSendOtp()}
                  disabled={(props.emailOtpSent && props.emailOtpCountdown > 0) || props.checkingForPreviousData}
                  className="px-4 py-2.5 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                >
                  {otpLoading || props.checkingForPreviousData ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {props.checkingForPreviousData ? "Verifying..." : "Sending..."}
                    </>
                  ) : props.emailOtpSent ? (
                    <>
                      <i className="ri-mail-send-line"></i>
                      {props.emailOtpCountdown > 0
                        ? `Resend (${props.emailOtpCountdown}s)`
                        : "Resend OTP"}
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line"></i>
                      Send OTP
                    </>
                  )}
                </button>
              )}
              {props.emailVerified && (
                <div className="px-4 py-2.5 bg-green-100 border border-green-200 rounded-lg text-sm text-green-700 font-medium flex items-center gap-1.5 whitespace-nowrap">
                  <i className="ri-shield-check-line"></i>
                  Verified
                </div>
              )}
            </div>

            {/* OTP Modal */}
            <OtpModal
              isOpen={props.emailOtpSent && !props.emailVerified}
              title="Email Verification"
              description={`Code sent to ${props.email}`}
              onClose={() => props.onCloseEmailOtp()}
              onVerify={async (otp) => {
                props.onEmailOtpCodeChange(otp);
                await handleVerifyOtp(otp);
              }}
              resendOtp={async () => {
                await props.onResendEmailOtp();
              }}
            />

            {props.errors.email && (
              <p className="text-xs text-red-500 mt-1">{props.errors.email}</p>
            )}

            {/* to show email error */}
            {errors.email && (
              <p className="text-xs text-red-500 mt-1">{errors.email}</p>
            )}

            {emailCheck.status === 'taken' && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1 font-medium">
                <i className="ri-error-warning-line"></i>
                {emailCheck.message}
              </p>
            )}
            {emailCheck.status === 'available' && !props.emailVerified && (
              <p className="text-xs text-teal-600 mt-1 flex items-center gap-1 font-medium">
                <i className="ri-checkbox-circle-line"></i>
                Email is available
              </p>
            )}


            {/* to show otp error */}
            {errors.otp && (
              <p className="text-xs text-red-500 mt-2">{errors.otp}</p>
            )}
            {/* chcek email verified or not */}
            {props.errors.emailVerified && (
              <div className="mt-2 flex items-center gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                <i className="ri-error-warning-line text-red-500 text-sm"></i>

                <p className="text-xs text-red-600">
                  {props.errors.emailVerified}
                </p>
              </div>
            )}
            {/* <p className="text-xs text-gray-400 mt-2">
              Dashboard access and reports will be sent to this email
            </p> */}

            {props.errors.apiError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
                <i className="ri-error-warning-line"></i>
                {props.errors.apiError}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100"></div>

      {/* Business Identity Block */}
      <div className={!allVerified ? 'opacity-40 pointer-events-none select-none' : ''}>

        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-teal-500/10 flex items-center justify-center">
            <i className="ri-building-line text-teal-600 text-sm"></i>
          </div>
          Business Identity
        </h3>

        <div className="space-y-5">
          {/* Business Name */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
              <span>Brand Name</span> <span className="text-red-500">*</span>
              <FieldInfo fieldKey="brandName" />
            </label>
            <input
              type="text"
              value={props.brandName}
              onChange={(e) => props.onBrandNameChange(e.target.value.replace(/[^a-zA-Z0-9\s#'-]/g, ''))}
              placeholder="e.g., Artisan Bakery Co."
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
            {props.errors.brandName && (
              <p className="text-xs text-red-500 mt-1">
                {props.errors.brandName}
              </p>
            )}
          </div>

          {/* Website URL with auto-validation */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
              <span>Website URL</span>
              <FieldInfo fieldKey="websiteUrl" />
            </label>
            <div className="relative">
              <input
                type="url"
                value={props.websiteUrl}
                onChange={(e) => props.onWebsiteUrlChange(e.target.value)}
                onBlur={() => {
                  if (props.websiteUrl.trim() && !props.websiteUrl.trim().startsWith("http")) {
                    props.onWebsiteUrlChange(`https://${props.websiteUrl.trim()}`);
                  }
                }}
                placeholder="https://www.yourbusiness.com"
                className={`w-full px-4 py-2.5 pr-10 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${props.websiteUrlStatus === "invalid"
                  ? "border-red-300 focus:ring-red-500"
                  : props.websiteUrlStatus === "valid"
                    ? "border-green-300 focus:ring-green-500"
                    : "border-gray-200"
                  }`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {props.websiteUrlStatus === "validating" && (
                  <div className="w-4 h-4 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"></div>
                )}
                {props.websiteUrlStatus === "valid" && (
                  <i className="ri-check-line text-green-500 text-lg"></i>
                )}
                {props.websiteUrlStatus === "invalid" && (
                  <i className="ri-error-warning-line text-red-500 text-lg"></i>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between mt-1">
              {/* <p className="text-xs text-gray-400">
                Auto-validation required — ensure accessibility and basic
                structure readiness
              </p> */}
              {props.websiteUrlStatus === "invalid" && (
                <p className="text-xs text-red-500">Please enter a valid URL</p>
              )}
            </div>
            {props.errors.websiteUrl && (
              <p className="text-xs text-red-500 mt-1">
                {props.errors.websiteUrl}
              </p>
            )}
          </div>

          {/* Business Description */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <span>Brand Description</span> <span className="text-red-500">*</span>
                <FieldInfo fieldKey="businessDescription" />
              </label>
              <button
                onClick={() => handleDescription()}
                disabled={!props.websiteUrl || props.websiteUrlStatus !== "valid" || descriptionLoading}
                className="text-sm text-teal-600 hover:text-teal-700 transition-colors cursor-pointer flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {descriptionLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                    Auto-Filling...
                  </>
                ) : (
                  <>
                    <i className="ri-magic-line"></i>
                    Auto-Fill from URL
                  </>
                )}
              </button>
            </div>
            {scrapeError && (
              <div className="flex items-center gap-1.5 mb-2 text-xs text-red-500">
                <i className="ri-error-warning-line shrink-0"></i>
                <span>{scrapeError}</span>
              </div>
            )}
            {descriptionLoading && slowHint && (
              <div className="flex items-center gap-1.5 mb-2 text-xs text-amber-600">
                <i className="ri-time-line shrink-0"></i>
                <span>Still working — this is taking longer than usual…</span>
              </div>
            )}
            {/* <p className="text-xs text-gray-500 mb-2">
              Describe your business, who it&apos;s for, and what people might
              type into Google to find you.
            </p> */}
            <textarea
              value={props.businessDescription}
              onChange={(e) =>
                props.onBusinessDescriptionChange(e.target.value.slice(0, 500))
              }
              placeholder="e.g., Local artisan bakery specializing in gluten-free cakes for health-conscious customers in downtown Seattle."
              rows={3}
              maxLength={500}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
            />
            {props.errors.businessDescription && (
              <p className="text-xs text-red-500 mt-1">
                {props.errors.businessDescription}
              </p>
            )}
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-gray-400">
                You can auto generate and further edit&nbsp;
              </p>
              <p className="text-xs text-gray-400">
                {props.businessDescription.length}/500
              </p>
            </div>
          </div>

          {/* Examples Box */}
          <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-2">
              Examples:
            </h4>
            <ul className="space-y-1.5 text-xs text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">•</span>
                &quot;Family owned Italian restaurant serving authentic pasta
                and pizza to locals in Brooklyn.&quot;
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">•</span>
                &quot;Digital marketing agency helping small businesses grow
                their online presence through SEO and social media.&quot;
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">•</span>
                &quot;Eco-friendly cleaning service for busy professionals in
                San Francisco Bay Area.&quot;
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100"></div>

      {/* Classification Block */}
      <div className={!allVerified ? 'opacity-40 pointer-events-none select-none' : ''}>
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-teal-500/10 flex items-center justify-center">
            <i className="ri-bar-chart-box-line text-teal-600 text-sm"></i>
          </div>
          Classification
        </h3>


        <div className="space-y-5">
          {/* Business Type */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
              <span>Business Type</span> <span className="text-red-500">*</span>
              <FieldInfo fieldKey="businessType" />
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                {
                  id: "service",
                  label: "Service",
                  icon: "ri-service-line",
                  desc: "B2B/B2C services",
                },
                {
                  id: "product",
                  label: "Product",
                  icon: "ri-shopping-bag-3-line",
                  desc: "Physical or digital goods",
                },
                {
                  id: "both",
                  label: "Both",
                  icon: "ri-stack-line",
                  desc: "Products + Services",
                },
              ].map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() =>
                    props.onBusinessTypeChange(type.id as BusinessType)
                  }
                  className={`flex flex-col items-center gap-1.5 p-4 border-2 rounded-lg transition-all cursor-pointer ${props.businessType === type.id
                    ? "border-teal-500 bg-teal-50"
                    : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                >
                  <i
                    className={`${type.icon} text-xl ${props.businessType === type.id ? "text-teal-600" : "text-gray-400"}`}
                  ></i>
                  <span
                    className={`text-sm font-medium ${props.businessType === type.id ? "text-teal-700" : "text-gray-700"}`}
                  >
                    {type.label}
                  </span>
                  {type.id === "both" && (
                    <span
                      className={`text-xs ${props.businessType === type.id ? "text-teal-600" : "text-gray-400"}`}
                    >
                      {type.desc}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {props.errors.businessType && (
              <p className="text-xs text-red-500 mt-2">
                {props.errors.businessType}
              </p>
            )}
            {/* <p className="text-xs text-gray-400 mt-2">
              Drives SEO engine, GMB optimization, and content personalization
            </p> */}
          </div>

          {/* Primary Business Category */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
              <span>Primary Business Category</span> <span className="text-red-500">*</span>
              <FieldInfo fieldKey="primaryCategory" />
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() =>
                  props.onShowCategoryDropdown(!props.showCategoryDropdown)
                }
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-left focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent flex items-center justify-between cursor-pointer bg-white"
              >
                <span
                  className={
                    props.primaryCategory ? "text-gray-900" : "text-gray-400"
                  }
                >
                  {props.primaryCategory
                    ? (primaryCategories.includes(props.primaryCategory) ? props.primaryCategory : "Other")
                    : "Select your primary category..."}
                </span>
                <i
                  className={`ri-arrow-down-s-line text-gray-400 transition-transform ${props.showCategoryDropdown ? "rotate-180" : ""}`}
                ></i>
              </button>
              {props.showCategoryDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 flex flex-col">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      type="text"
                      placeholder="Search categories..."
                      value={categorySearchQuery}
                      onChange={(e) => setCategorySearchQuery(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    {(primaryCategories.filter(cat => cat.toLowerCase().includes(categorySearchQuery.toLowerCase())).length > 0
                      ? primaryCategories.filter(cat => cat.toLowerCase().includes(categorySearchQuery.toLowerCase()))
                      : ["Other"]
                    ).map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => {
                          props.onPrimaryCategoryChange(cat);
                          props.onShowCategoryDropdown(false);
                          setCategorySearchQuery("");
                        }}
                        className={`w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 cursor-pointer ${props.primaryCategory === cat || (!primaryCategories.includes(props.primaryCategory) && cat === "Other" && props.primaryCategory !== "")
                          ? "bg-teal-50 text-teal-700 font-medium"
                          : "text-gray-700"
                          }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {(!primaryCategories.includes(props.primaryCategory) && props.primaryCategory !== "" || props.primaryCategory === "Other") && (
              <div className="mt-3 animate-in fade-in slide-in-from-top-2">
                <input
                  type="text"
                  value={props.primaryCategory === "Other" ? "" : props.primaryCategory}
                  onChange={(e) => props.onPrimaryCategoryChange(e.target.value)}
                  placeholder="Enter your custom business category"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                />
              </div>
            )}

            {props.errors.primaryCategory && (
              <p className="text-xs text-red-500 mt-1">
                {props.errors.primaryCategory}
              </p>
            )}
            {/* <p className="text-xs text-gray-400 mt-2">
              Drives the keyword engine and competitor mapping
            </p> */}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100"></div>

      {/* Managed by Agency */}
      <div className={!allVerified ? 'opacity-40 pointer-events-none select-none' : ''}>
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center">
            <i className="ri-team-line text-gray-600 text-sm"></i>
          </div>
          Agency Management
        </h3>

        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
                <i className="ri-briefcase-line text-gray-600 text-lg"></i>
              </div>

              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                  <span>Is your Brand Managed by an Agency?</span> <span className="text-red-500">*</span>
                  <FieldInfo fieldKey="isManagedByAgency" />
                </label>

                {/* <p className="text-xs text-gray-500">
                  Enable if an external agency handles your marketing activities
                </p> */}
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                props.onIsManagedByAgency(!props.isManagedByAgency)
              }
              className={`relative w-11 h-6 rounded-full transition-colors duration-300 cursor-pointer ${props.isManagedByAgency ? "bg-teal-500" : "bg-gray-300"
                }`}
            >
              <div
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${props.isManagedByAgency ? "translate-x-5" : "translate-x-0"
                  }`}
              />
            </button>
          </div>
        </div>

        {props.isManagedByAgency && (
          <div className="mt-4 space-y-4 animate-in">
            {/* Select from Foresight Agencies */}
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-search-line text-teal-600"></i>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <span>Find Your Agency</span>
                  <FieldInfo fieldKey="findAgency" />
                </label>
              </div>
              {/* <p className="text-xs text-gray-500 mb-4">
                Select from agencies already using Marketing 4Sight
              </p> */}

              <div className="relative">
                <button
                  type="button"
                  onClick={() =>
                    props.onShowAgencyDropdown(!props.showAgencyDropdown)
                  }
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-left focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent flex items-center justify-between cursor-pointer"
                >
                  <span
                    className={
                      props.selectedAgencyFromList
                        ? "text-gray-900"
                        : "text-gray-400"
                    }
                  >
                    {props.selectedAgencyFromList
                      ? foresightAgencies.find(
                        (a) => a.id === props.selectedAgencyFromList,
                      )?.name || props.agencyName || "Select an agency..."
                      : "Select an agency..."}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-gray-400 transition-transform ${props.showAgencyDropdown ? "rotate-180" : ""}`}
                  ></i>
                </button>
                {props.showAgencyDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-56 overflow-y-auto">
                    {foresightAgencies.map((agency) => (
                      <button
                        key={agency.id}
                        type="button"
                        onClick={() => {
                          props.onSelectedAgencyFromList(agency.id);
                          props.onAgencyId(agency.id);
                          props.onAgencyNotListed(false);
                          props.onAgencyName(agency.name);
                          props.onAgencyEmail(agency.email);
                          props.onShowAgencyDropdown(false);
                        }}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${props.selectedAgencyFromList === agency.id
                          ? "bg-teal-50"
                          : ""
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {agency.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {agency.email}
                            </p>
                          </div>
                          {props.selectedAgencyFromList === agency.id && (
                            <i className="ri-check-line text-teal-500"></i>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {props.errors.agencyId && !props.agencyNotListed && (
                <p className="text-xs text-red-500 mt-2">
                  {props.errors.agencyId}
                </p>
              )}

              {/* Selected Agency Preview */}
              {props.selectedAgencyFromList && !props.agencyNotListed && (
                <div className="mt-4 bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center">
                        <i className="ri-building-line text-white text-sm"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {
                            foresightAgencies.find(
                              (a) => a.id === props.selectedAgencyFromList,
                            )?.name || props.agencyName
                          }
                        </p>
                        <p className="text-xs text-gray-500">
                          Verified Foresight Partner
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        props.onSelectedAgencyFromList(null);
                        props.onAgencyId("");
                        props.onAgencyName("");
                        props.onAgencyContactName("");
                        props.onAgencyEmail("");
                        props.onAgencyPhone("");
                        props.onAgencyWebsite("");
                      }}
                      className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer"
                    >
                      Change
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="text-gray-500">Email</span>
                      <p className="text-gray-900 font-medium">
                        {
                          foresightAgencies.find(
                            (a) => a.id === props.selectedAgencyFromList,
                          )?.email || props.agencyEmail
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Not Listed Toggle */}
              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    props.onAgencyNotListed(!props.agencyNotListed);
                    if (!props.agencyNotListed) {
                      props.onSelectedAgencyFromList(null);
                      props.onAgencyName("");
                      props.onAgencyContactName("");
                      props.onAgencyEmail("");
                      props.onAgencyPhone("");
                      props.onAgencyWebsite("");
                    }
                  }}
                  className={`flex items-center gap-2 text-sm font-medium cursor-pointer transition-colors ${props.agencyNotListed
                    ? "text-teal-600"
                    : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  <div
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${props.agencyNotListed
                      ? "border-teal-500 bg-teal-500"
                      : "border-gray-300"
                      }`}
                  >
                    {props.agencyNotListed && (
                      <i className="ri-check-line text-white text-xs"></i>
                    )}
                  </div>
                  My agency is not listed
                </button>
              </div>
            </div>

            {/* Manual Agency Info */}
            {props.agencyNotListed && (
              <div className="bg-white border border-gray-200 rounded-lg p-5">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-building-line text-teal-600"></i>
                  <h4 className="text-sm font-semibold text-gray-900">
                    Agency Information
                  </h4>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1.5">
                        <span>Agency Name</span> <span className="text-red-500">*</span>
                        <FieldInfo fieldKey="agencyName" />
                      </label>
                      <input
                        type="text"
                        value={props.agencyName}
                        onChange={(e) => props.onAgencyName(e.target.value.slice(0, 60))}
                        maxLength={60}
                        placeholder="Enter agency name"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                      {props.errors.agencyName && (
                        <p className="text-xs text-red-500 mt-1">
                          {props.errors.agencyName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1.5">
                        <span>Contact Person</span> <span className="text-red-500">*</span>
                        <FieldInfo fieldKey="agencyContactName" />
                      </label>
                      <input
                        type="text"
                        value={props.agencyContactName}
                        onChange={(e) =>
                          props.onAgencyContactName(e.target.value.replace(/[^a-zA-Z\s]/g, ''))
                        }
                        placeholder="Contact person name"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                      {props.errors.agencyContactName && (
                        <p className="text-xs text-red-500 mt-1">
                          {props.errors.agencyContactName}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1.5">
                        <span>Agency Email</span> <span className="text-red-500">*</span>
                        <FieldInfo fieldKey="agencyEmail" />
                      </label>
                      <input
                        type="email"
                        value={props.agencyEmail}
                        onChange={(e) => props.onAgencyEmail(e.target.value)}
                        placeholder="agency@example.com"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                      {props.errors.agencyEmail && (
                        <p className="text-xs text-red-500 mt-1">
                          {props.errors.agencyEmail}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1.5">
                        <span>Phone Number</span> <span className="text-red-500">*</span>
                        <FieldInfo fieldKey="agencyPhone" />
                      </label>
                      {/* <input
                        type="tel"
                        value={props.agencyPhone}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^\d+()\s-]/g, "");
                          props.onAgencyPhone(val);
                        }}
                        placeholder="+1 555-000-0000"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      /> */}
                      <PhoneNumberInput
                        value={props.agencyPhone}
                        onChange={props.onAgencyPhone}
                        hasError={!!props.errors.agencyPhone}
                        countryCodes={countryCodes}
                        maxLength={10}
                      />
                      {props.errors.agencyPhone && (
                        <p className="text-xs text-red-500 mt-1">
                          {props.errors.agencyPhone}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1.5">
                      <span>Agency Website</span>
                      <FieldInfo fieldKey="agencyWebsite" />
                    </label>
                    <input
                      type="url"
                      value={props.agencyWebsite}
                      onChange={(e) => props.onAgencyWebsite(e.target.value)}
                      onBlur={() => {
                        if (props.agencyWebsite.trim() && !props.agencyWebsite.trim().startsWith("http")) {
                          props.onAgencyWebsite(`https://${props.agencyWebsite.trim()}`);
                        }
                      }}
                      placeholder="https://agency-website.com"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    />
                    {props.errors.agencyWebsite && (
                      <p className="text-xs text-red-500 mt-1">
                        {props.errors.agencyWebsite}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
